import React, { useMemo, useState, useEffect } from 'react';
import { Plus, Upload, Search, RotateCcw, Pencil, Trash2, Copy, Eye } from 'lucide-react';
import type { CMSData, Property, Inquiry, MediaFile, UserRole } from '../../types/cms';
import {
  districts,
  categories,
  listingTypes,
  salePriceOptions,
  rentPriceOptions,
  filterProperties,
  type PropertyFilterState,
  defaultPropertyFilters,
} from '../../utils/propertyFilters';
import { categoryLabels, districtLabels, listingLabels } from '../../utils/helpers';
import { PropertyStatusBadge } from '../common/PropertyStatusBadge';
import { PropertyEditor } from './PropertyEditor';
import { OwnerListingApprovals } from './OwnerListingApprovals';
import { smartCompressImage } from '../../lib/imageCompression';
import { applyDiagonalWatermark } from '../../lib/imageWatermark';

interface PropertyManagerProps {
  draft: CMSData;
  setDraft: React.Dispatch<React.SetStateAction<CMSData>>;
  saveDraft: (data: CMSData) => Promise<boolean>;
  token: string;
  apiBase: string;
  media: MediaFile[];
  fetchMedia: () => void;
  showMessage: (msg: string, type?: 'success' | 'error') => void;
  onAudit?: (action: string, resource?: string, resourceId?: string, details?: Record<string, any>) => Promise<void>;
  currentUsername?: string;
  role?: UserRole;
  usedMediaIds?: Set<string>;
  watermarkOpacity?: number;
  onDeleteMedia?: (id: string) => Promise<void>;
}

const ITEMS_PER_PAGE = 20;

const emptyProperty = (
  agents: CMSData['agents'],
  createdBy?: string,
  defaultAgentId?: string
): Property => ({
  id: `p${Date.now()}`,
  code: '',
  title: '',
  listingType: 'sale',
  category: 'office',
  district: 'kwai_chung',
  location: '',
  address: '',
  price: 0,
  priceUnit: 'HKD',
  areaGross: 0,
  description: '',
  images: [],
  tags: [],
  features: [],
  featured: false,
  status: 'available',
  agentId: defaultAgentId || agents[0]?.id || '',
  createdBy,
});

const inquiryToProperty = (inq: Inquiry, agents: CMSData['agents'], createdBy?: string): Property => {
  const priceNum = inq.expectedPrice ? Number(inq.expectedPrice.replace(/[^0-9.]/g, '')) : 0;
  return {
    id: `p${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    code: '',
    title: inq.address ? `${inq.address} 放盤` : '業主放盤',
    listingType: (inq.listingType as Property['listingType']) || 'sale',
    category: (inq.propertyType as Property['category']) || 'office',
    district: 'kwai_chung',
    location: '',
    address: inq.address || '',
    price: priceNum || 0,
    priceUnit: 'HKD',
    areaGross: inq.area ? Number(inq.area.replace(/[^0-9.]/g, '')) : 0,
    description: inq.message || '',
    images: [],
    tags: [],
    features: [],
    featured: false,
    status: 'available',
    agentId: agents[0]?.id || '',
    createdBy,
  };
};

export const PropertyManager: React.FC<PropertyManagerProps> = ({
  draft,
  setDraft,
  saveDraft,
  token,
  apiBase,
  media,
  fetchMedia,
  showMessage,
  onAudit,
  currentUsername,
  role,
  usedMediaIds,
  onDeleteMedia,
  watermarkOpacity,
}) => {
  const isAdmin = role === 'admin';
  const [filters, setFilters] = useState<PropertyFilterState>(defaultPropertyFilters);
  const [activeFilters, setActiveFilters] = useState<PropertyFilterState>(defaultPropertyFilters);
  const [editorProperty, setEditorProperty] = useState<Property | null>(null);
  const [editorMode, setEditorMode] = useState<'create' | 'edit' | 'approve'>('edit');
  const [activeSubTab, setActiveSubTab] = useState<'properties' | 'approvals'>('properties');
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [approvingInquiryId, setApprovingInquiryId] = useState<string | null>(null);
  const [staffUsers, setStaffUsers] = useState<{ id: string; username: string; role: UserRole; agentId?: string }[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchInquiries = async () => {
    const res = await fetch(`${apiBase}/api/inquiries`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) setInquiries(await res.json());
  };

  useEffect(() => {
    fetchInquiries();
  }, [token, apiBase]);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${apiBase}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.users)) {
        setStaffUsers(data.users);
      } else {
        console.error('[PropertyManager] fetchUsers unexpected response:', data);
      }
    } catch (err) {
      console.error('[PropertyManager] fetchUsers error:', err);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchUsers();
  }, [token, apiBase]);

  // 物業流量成效（FB 短鏈點擊＋前台瀏覽——批量載入，列表顯示）
  const [analyticsMap, setAnalyticsMap] = useState<Record<string, { views: number; fbClicks: number }>>({});
  useEffect(() => {
    if (!token) return;
    fetch(`${apiBase}/api/analytics/properties`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && typeof d === 'object') setAnalyticsMap(d);
      })
      .catch(() => {});
  }, [token, apiBase]);

  const filteredProperties = useMemo(
    () => filterProperties(draft.properties, activeFilters),
    [draft.properties, activeFilters]
  );

  const normalize = (s: string | undefined) => (s || '').trim().toLowerCase();

  const canManageProperty = (property: Property) =>
    isAdmin || normalize(property.createdBy) === normalize(currentUsername);

  const visibleProperties = useMemo(
    () =>
      isAdmin
        ? filteredProperties
        : filteredProperties.filter((p) => normalize(p.createdBy) === normalize(currentUsername)),
    [filteredProperties, isAdmin, currentUsername]
  );

  const totalPages = Math.max(1, Math.ceil(visibleProperties.length / ITEMS_PER_PAGE));
  const paginatedProperties = useMemo(
    () => visibleProperties.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [visibleProperties, currentPage]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilters]);

  // Staff 除錯日誌：幫助排查為什麼看不到物業
  useEffect(() => {
    if (isAdmin) return;
    console.log('[Staff Debug] currentUsername:', currentUsername);
    console.log('[Staff Debug] total properties:', draft.properties.length);
    console.log('[Staff Debug] visible properties:', visibleProperties.length);
    console.log(
      '[Staff Debug] properties createdBy:',
      draft.properties.map((p) => ({ id: p.id, code: p.code, createdBy: p.createdBy }))
    );
  }, [isAdmin, currentUsername, draft.properties, visibleProperties.length]);

  const updateFilter = <K extends keyof PropertyFilterState>(key: K, value: PropertyFilterState[K]) => {
    setFilters((f) => ({ ...f, [key]: value }));
  };

  const applyFilters = () => setActiveFilters(filters);
  const resetFilters = () => {
    setFilters(defaultPropertyFilters);
    setActiveFilters(defaultPropertyFilters);
  };

  const handleImportProperties = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const imported = JSON.parse(text) as Property[];
      if (!Array.isArray(imported)) throw new Error('格式錯誤：必須是物業陣列');
      const normalized = imported.map((p, i) => ({
        ...p,
        id: p.id || `import_${Date.now()}_${i}`,
        code: p.code || `ML-IMP-${i + 1}`,
        images: Array.isArray(p.images) ? p.images : [],
        tags: Array.isArray(p.tags) ? p.tags : [],
        features: Array.isArray(p.features) ? p.features : [],
      }));
      setDraft((prev) => ({ ...prev, properties: [...prev.properties, ...normalized] }));
      showMessage(`成功導入 ${normalized.length} 個物業`);
    } catch (err) {
      showMessage(`導入失敗：${String(err)}`);
    }
  };

  const addProperty = () => {
    // Staff 新增物業時，預設負責人為自己連結的 Agent，避免 createdBy 被覆蓋
    const ownAgentId = staffUsers?.find(
      (u) => u.username.trim().toLowerCase() === (currentUsername || '').trim().toLowerCase()
    )?.agentId;
    setEditorProperty(emptyProperty(draft.agents, currentUsername, ownAgentId));
    setEditorMode('create');
  };

  const editProperty = (property: Property) => {
    if (!canManageProperty(property)) {
      showMessage('你沒有權限編輯此物業', 'error');
      return;
    }
    setEditorProperty(property);
    setEditorMode('edit');
  };

  const handleSaveProperty = async (property: Property) => {
    const exists = !!draft.properties.find((p) => p.id === property.id);
    const isCreate = editorMode === 'create' || !exists;

    // 2026-08-26 改造：改走單筆即時 API（伺服器端 read-modify-write），
    // 不再把本地草稿整份覆寫——根治多 Agent 併發時互相覆蓋物業/圖片引用的問題
    // 2026-08-27 進化：更新走欄位級 PATCH（只送有變更的欄位）——兩位同事同時編輯
    // 同一物業的不同欄位時，伺服器各自合併、互不覆蓋（整份物件會含過期欄位必然蓋掉他人）
    const original = draft.properties.find((p) => p.id === property.id);
    let body: unknown = property;
    if (!isCreate && original) {
      const set: Record<string, unknown> = {};
      for (const key of Object.keys(property) as (keyof Property)[]) {
        if (JSON.stringify(property[key]) !== JSON.stringify(original[key])) {
          set[key] = property[key];
        }
      }
      body = { id: property.id, set };
    }
    try {
      const res = await fetch(
        isCreate ? `${apiBase}/api/admin/properties` : `${apiBase}/api/admin/properties/${property.id}`,
        {
          method: isCreate ? 'POST' : 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        }
      );
      if (res.status === 401) {
        showMessage('登入已過期，請重新登入', 'error');
        localStorage.removeItem('marveland_admin_token');
        localStorage.removeItem('marveland_admin_role');
        localStorage.removeItem('marveland_admin_username');
        localStorage.removeItem('marveland_admin_userid');
        window.location.reload();
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        showMessage(`儲存失敗：${data?.error || `HTTP ${res.status}`}`, 'error');
        return;
      }

      // 本地 draft 同步（僅為即時 UI；真相在伺服器）
      setDraft((prev) => {
        const list = prev.properties.some((p) => p.id === property.id)
          ? prev.properties.map((p) => (p.id === property.id ? property : p))
          : [property, ...prev.properties];
        return { ...prev, properties: list };
      });

      if (editorMode === 'approve' && approvingInquiryId) {
        try {
          await fetch(`${apiBase}/api/inquiries/${approvingInquiryId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status: 'approved' }),
          });
          fetchInquiries();
        } catch {
          // non-blocking
        }
        setApprovingInquiryId(null);
      }

      setEditorProperty(null);
      showMessage(isCreate ? '新盤已建立並即時上線' : '變更已即時儲存並上線');
    } catch (e) {
      showMessage(`儲存失敗：${e instanceof Error ? e.message : String(e)}`, 'error');
    }
  };

  const removeProperty = async (id: string) => {
    if (!confirm('確定刪除此物業？')) return;
    const target = draft.properties.find((p) => p.id === id);
    if (!target) return;
    if (!canManageProperty(target)) {
      showMessage('你沒有權限刪除此物業', 'error');
      return;
    }

    // 收集該物業使用的媒體 ID
    // 2026-08-27 用戶定案：伺服器端刪除時自動連根拔起無人引用的照片（purgeUnusedMedia 以最新資料核實，
    // 仍被其他物業/網站區塊引用的自動保留）——前端不再自行清理，避免競態誤刪
    try {
      const res = await fetch(`${apiBase}/api/admin/properties/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        showMessage('登入已過期，請重新登入', 'error');
        localStorage.removeItem('marveland_admin_token');
        localStorage.removeItem('marveland_admin_role');
        localStorage.removeItem('marveland_admin_username');
        localStorage.removeItem('marveland_admin_userid');
        window.location.reload();
        return;
      }
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        showMessage(`刪除失敗：${data?.error || `HTTP ${res.status}`}`, 'error');
        return;
      }

      setDraft({ ...draft, properties: draft.properties.filter((p) => p.id !== id) });
      fetchMedia();
      const purge = (await res.json().catch(() => null))?.purge;
      if (purge && (purge.purged?.length || purge.keptInUse) && Object.keys(purge.keptInUse || {}).length + (purge.purged?.length || 0) > 0) {
        const kept = Object.keys(purge.keptInUse || {}).length;
        showMessage(`已刪除物業；${purge.purged?.length || 0} 張照片已從媒體庫清除${kept ? `，${kept} 張仍被其他頁面使用而保留` : ''}`);
      } else {
        showMessage('已刪除物業');
      }

      if (onAudit) {
        await onAudit('property_delete', 'property', id, {
          title: target.title,
          code: target.code,
          by: currentUsername,
        });
      }
    } catch (err) {
      showMessage(`刪除失敗：${err instanceof Error ? err.message : String(err)}`, 'error');
    }
  };

  const duplicateProperty = async (property: Property) => {
    const copy: Property = {
      ...property,
      id: `p${Date.now()}`,
      code: `${property.code}-copy`,
      title: `${property.title}（複製）`,
      featured: false,
    };
    setDraft((prev) => ({ ...prev, properties: [...prev.properties, copy] }));
    showMessage('已複製');
    if (onAudit) {
      await onAudit('property_duplicate', 'property', copy.id, {
        sourceId: property.id,
        title: copy.title,
        by: currentUsername,
      });
    }
  };

  const handleApproveInquiry = (inq: Inquiry) => {
    setApprovingInquiryId(inq.id);
    setEditorProperty(inquiryToProperty(inq, draft.agents, currentUsername));
    setEditorMode('approve');
  };

  const handleUploadMedia = async (
    file: File,
    meta?: { category?: string; tags?: string[] }
  ): Promise<string | undefined> => {
    let compressed;
    try {
      // 防盜水印（中央對角 MARVELAND）先於壓縮套用——濃度由後台「防盜水印濃度」設定
      const marked = await applyDiagonalWatermark(file, watermarkOpacity);
      compressed = await smartCompressImage(marked);
    } catch (err) {
      showMessage(`圖片壓縮失敗：${err instanceof Error ? err.message : String(err)}`);
      throw err;
    }
    const formData = new FormData();
    formData.append('file', compressed.file);
    if (meta?.category) formData.append('category', meta.category);
    if (meta?.tags?.length) formData.append('tags', JSON.stringify(meta.tags));
    const res = await fetch(`${apiBase}/api/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (res.status === 401) {
      showMessage('登入已過期，請重新登入');
      localStorage.removeItem('marveland_admin_token');
      localStorage.removeItem('marveland_admin_role');
      localStorage.removeItem('marveland_admin_username');
      localStorage.removeItem('marveland_admin_userid');
      window.location.reload();
      throw new Error('Unauthorized');
    }
    if (res.ok) {
      const data = await res.json();
      showMessage(`上傳成功${compressed.isCompressed ? `（壓縮 ${compressed.ratio}）` : ''}`);
      fetchMedia();
      return data?.media?.id ? `${apiBase}/api/media/${data.media.id}` : undefined;
    }
    let errorText = '';
    try {
      const data = await res.json();
      errorText = data.error || `HTTP ${res.status}`;
    } catch {
      errorText = `HTTP ${res.status}`;
    }
    showMessage(`上傳失敗：${errorText}`);
    throw new Error(errorText);
  };

  const agentName = (id: string) => draft.agents.find((a) => a.id === id)?.name || '未分配';

  return (
    <div className="space-y-6">
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setActiveSubTab('properties')}
            className={`rounded-lg px-4 py-2 text-sm font-bold ${
              activeSubTab === 'properties' ? 'bg-blue-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            現有物業
          </button>
          <button
            onClick={() => setActiveSubTab('approvals')}
            className={`rounded-lg px-4 py-2 text-sm font-bold ${
              activeSubTab === 'approvals' ? 'bg-blue-800 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            業主放盤申請
            {inquiries.filter((i) => i.type === 'owner_listing' && i.status === 'new').length > 0 && (
              <span className="ml-2 rounded-full bg-red-500 px-2 py-0.5 text-[10px] text-white">
                {inquiries.filter((i) => i.type === 'owner_listing' && i.status === 'new').length}
              </span>
            )}
          </button>
        </div>
      )}

      {activeSubTab === 'properties' && (
        <>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={addProperty}
                className="flex items-center gap-2 rounded bg-blue-800 px-4 py-2 text-sm font-bold text-white hover:bg-blue-900"
              >
                <Plus className="h-4 w-4" />
                新增物業
              </button>
              {isAdmin && (
                <label className="flex cursor-pointer items-center gap-2 rounded bg-slate-700 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800">
                  <Upload className="h-4 w-4" />
                  批量導入 JSON
                  <input type="file" accept=".json,application/json" className="hidden" onChange={handleImportProperties} />
                </label>
              )}
            </div>
            <p className="text-sm text-slate-500">
              共 <span className="font-bold text-slate-900">{visibleProperties.length}</span> 個結果 / 總數 {' '}
              {isAdmin ? draft.properties.length : draft.properties.filter((p) => normalize(p.createdBy) === normalize(currentUsername)).length}
            </p>
          </div>

          {!isAdmin && (
            <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
              <span className="font-bold">提示：</span>你目前以 <span className="font-bold">{currentUsername}</span> 登入，只會顯示屬於你的物業。如果 Admin 剛指派物業給你，請確認物業的「創建者 / 權限歸屬」已儲存為 <span className="font-bold">{currentUsername}</span>。
            </div>
          )}

          {/* Filters */}
          <div className="rounded-xl bg-white p-5 shadow-sm">
            <div className="mb-4 text-sm font-bold text-slate-700">篩選條件</div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-500">租售類型</label>
                <select
                  value={filters.listingType}
                  onChange={(e) => updateFilter('listingType', e.target.value as PropertyFilterState['listingType'])}
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-800"
                >
                  {listingTypes.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-500">地區</label>
                <select
                  value={filters.district}
                  onChange={(e) => updateFilter('district', e.target.value as PropertyFilterState['district'])}
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-800"
                >
                  {districts.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-500">物業類型</label>
                <select
                  value={filters.category}
                  onChange={(e) => updateFilter('category', e.target.value as PropertyFilterState['category'])}
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-800"
                >
                  {categories.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-500">售價範圍</label>
                <select
                  value={filters.salePriceRange}
                  onChange={(e) => updateFilter('salePriceRange', e.target.value)}
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-800"
                >
                  {salePriceOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-500">租金範圍</label>
                <select
                  value={filters.rentPriceRange}
                  onChange={(e) => updateFilter('rentPriceRange', e.target.value)}
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-800"
                >
                  {rentPriceOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              {isAdmin && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-slate-500">負責人</label>
                  <select
                    value={filters.agentId}
                    onChange={(e) => updateFilter('agentId', e.target.value)}
                    className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-800"
                  >
                    <option value="all">所有負責人</option>
                    {draft.agents.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-500">狀態</label>
                <select
                  value={filters.featured}
                  onChange={(e) => updateFilter('featured', e.target.value as PropertyFilterState['featured'])}
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-800"
                >
                  <option value="all">所有狀態</option>
                  <option value="yes">精選</option>
                  <option value="no">非精選</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-500">關鍵字</label>
                <input
                  type="search"
                  value={filters.keyword}
                  onChange={(e) => updateFilter('keyword', e.target.value)}
                  placeholder="編號 / 標題 / 地址"
                  className="rounded border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-800"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button
                onClick={resetFilters}
                className="flex items-center gap-2 rounded border border-slate-300 px-4 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50"
              >
                <RotateCcw className="h-4 w-4" />
                重設
              </button>
              <button
                onClick={applyFilters}
                className="flex items-center gap-2 rounded bg-blue-800 px-4 py-2 text-sm font-bold text-white hover:bg-blue-900"
              >
                <Search className="h-4 w-4" />
                篩選
              </button>
            </div>
          </div>

          {/* List */}
          <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left">編號</th>
                  <th className="px-4 py-3 text-left">封面</th>
                  <th className="px-4 py-3 text-left">標題</th>
                  <th className="px-4 py-3 text-left">類型</th>
                  <th className="px-4 py-3 text-left">租售</th>
                  <th className="px-4 py-3 text-left">價錢</th>
                  <th className="px-4 py-3 text-left">面積</th>
                  <th className="px-4 py-3 text-left">成效</th>
                  <th className="px-4 py-3 text-left">負責人</th>
                  <th className="px-4 py-3 text-left">精選</th>
                  <th className="px-4 py-3 text-left">操作</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProperties.map((property) => (
                  <tr key={property.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold">{property.code}</td>
                    <td className="px-4 py-3">
                      {property.images[0] ? (
                        <img src={property.images[0]} alt="" className="h-12 w-16 rounded object-cover" />
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-semibold">{property.title}</p>
                      <p className="text-xs text-slate-500">{property.location} · {property.address}</p>
                    </td>
                    <td className="px-4 py-3">{categoryLabels[property.category]}</td>
                    <td className="px-4 py-3">
                      {property.status !== 'available' ? (
                        <PropertyStatusBadge status={property.status} pulse={false} />
                      ) : (
                        listingLabels[property.listingType]
                      )}
                      {property.fbPublishedAt ? (
                        <span className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">已推廣</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      {property.listingType === 'sale_or_rent'
                        ? `售 $${property.price.toLocaleString()} / 租 $${(property.rentPrice || 0).toLocaleString()}`
                        : property.listingType === 'rent' && property.rentPrice
                        ? `$${property.rentPrice.toLocaleString()}/月`
                        : `$${property.price.toLocaleString()}`}
                    </td>
                    <td className="px-4 py-3">{property.areaGross.toLocaleString()} 呎</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {(() => {
                        const a = analyticsMap[property.id];
                        if (!a || (a.views === 0 && a.fbClicks === 0)) return <span className="text-xs text-slate-300">—</span>;
                        return (
                          <span className="text-xs">
                            <span className="rounded bg-blue-100 px-1.5 py-0.5 font-semibold text-blue-700">↗ {a.fbClicks}</span>
                            <span className="ml-1 rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-600">👁 {a.views}</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">{agentName(property.agentId)}</td>
                    <td className="px-4 py-3">
                      {property.featured ? (
                        <span className="rounded bg-gold-100 px-2 py-1 text-xs font-bold text-gold-700">精選</span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => editProperty(property)}
                          disabled={!canManageProperty(property)}
                          className={`rounded p-1.5 text-white ${
                            canManageProperty(property)
                              ? 'bg-blue-800 hover:bg-blue-900'
                              : 'cursor-not-allowed bg-slate-300'
                          }`}
                          title={canManageProperty(property) ? '編輯' : '你沒有權限編輯此物業'}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => window.open(`/properties/${property.id}`, '_blank', 'noopener,noreferrer')}
                          className="rounded bg-emerald-100 p-1.5 text-emerald-700 hover:bg-emerald-200"
                          title="前台預覽"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => duplicateProperty(property)}
                          className="rounded bg-gold-100 p-1.5 text-gold-700 hover:bg-gold-200"
                          title="複製"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                        {canManageProperty(property) && (
                          <button
                            onClick={() => removeProperty(property.id)}
                            className="rounded bg-red-100 p-1.5 text-red-600 hover:bg-red-200"
                            title="刪除"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {visibleProperties.length > 0 && (
              <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
                <p className="text-sm text-slate-500">
                  顯示第 <span className="font-bold text-slate-900">{Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, visibleProperties.length)}</span> 至 {' '}
                  <span className="font-bold text-slate-900">{Math.min(currentPage * ITEMS_PER_PAGE, visibleProperties.length)}</span> 項，
                  共 <span className="font-bold text-slate-900">{visibleProperties.length}</span> 項
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="rounded border border-slate-300 px-3 py-1.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    上一頁
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`rounded px-3 py-1.5 text-sm font-bold ${
                        page === currentPage
                          ? 'bg-blue-800 text-white'
                          : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="rounded border border-slate-300 px-3 py-1.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    下一頁
                  </button>
                </div>
              </div>
            )}

            {visibleProperties.length === 0 && (
              <div className="px-6 py-12 text-center text-slate-500">
                {isAdmin ? (
                  '沒有符合條件的物業'
                ) : (
                  <div className="space-y-3">
                    <p>你尚未創建或被指派任何物業</p>
                    <div className="mx-auto max-w-md rounded-lg bg-slate-50 p-4 text-left text-xs text-slate-600">
                      <p className="mb-2 font-bold">除錯資訊（請截圖給管理員）：</p>
                      <p>你的登入帳號：<span className="font-mono font-bold text-slate-900">{currentUsername}</span></p>
                      <p>系統物業總數：<span className="font-mono font-bold text-slate-900">{draft.properties.length}</span></p>
                      <p className="mt-2 font-bold">各物業的 createdBy 值：</p>
                      {draft.properties.length === 0 ? (
                        <p>（系統中沒有任何物業）</p>
                      ) : (
                        <ul className="mt-1 max-h-40 overflow-auto space-y-1">
                          {draft.properties.map((p) => (
                            <li key={p.id} className="font-mono">
                              {p.code || p.id}: createdBy = {p.createdBy ? `"${p.createdBy}"` : '（空白 / 未分配）'}
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="mt-2 text-gold-700">
                        如果 Admin 已將物業指派給你，請確認該物業的 createdBy 與你的登入帳號完全一致（含大小寫）。
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {activeSubTab === 'approvals' && (
        <OwnerListingApprovals
          inquiries={inquiries}
          token={token}
          apiBase={apiBase}
          agents={draft.agents}
          onApprove={handleApproveInquiry}
          onRefresh={fetchInquiries}
          showMessage={showMessage}
        />
      )}

      {editorProperty && (
        <PropertyEditor
          key={editorMode === 'create' ? 'create' : editorProperty?.id}
          property={editorProperty}
          agents={draft.agents}
          media={media}
          apiBase={apiBase}
          token={token}
          onSave={handleSaveProperty}
          onCancel={() => {
            setEditorProperty(null);
            setApprovingInquiryId(null);
          }}
          onUploadMedia={handleUploadMedia}
          onDeleteMedia={onDeleteMedia}
          usedMediaIds={usedMediaIds}
          onRefreshMedia={fetchMedia}
          mode={editorMode}
          isAdmin={isAdmin}
          staffUsers={staffUsers}
        />
      )}
    </div>
  );
};
